import EditIcon from '@mui/icons-material/Edit';
import classnames from 'classnames';
import React from 'react';
import Input from './components/Input';
import { EditTextPropTypes } from './propTypes';
import styles from './styles.module.css';

export default function EditText({
    id,
    name,
    className,
    placeholder = '',
    inline = false,
    style = {},
    readonly = false,
    type = 'text',
    value,
    defaultValue,
    formatValue = (x) => x,
    parseValue = (x) => x,
    formatDisplayText = (x) => x,
    callbackArguments = {},
    onEditMode = () => { },
    onChange = () => { },
    onSave = () => { },
    onBlur = () => { },
    showEditButton = false,
    editButtonContent = <EditIcon />,
    editButtonProps = {},
    inputClassName
}) {
    const inputRef = React.useRef(null);
    const [changeEvent, setChangeEvent] = React.useState({});
    const [previousFormattedValue, setPreviousFormattedValue] = React.useState('');
    const [formattedValue, setFormattedValue] = React.useState('');
    const [editMode, setEditMode] = React.useState(false);

    React.useEffect(() => {
        if (defaultValue !== undefined) {
            // Format the defaultValue for display using formatValue
            const formattedValue = formatValue(defaultValue, callbackArguments);
            setPreviousFormattedValue(formattedValue);
            setFormattedValue(formattedValue);
        }
    }, [defaultValue, formatValue, callbackArguments]);

    React.useEffect(() => {
        if (value !== undefined) {
            // Format the value for display using formatValue
            const formattedValue = formatValue(value, callbackArguments);
            setFormattedValue(formattedValue);
            if (!editMode) {
                setPreviousFormattedValue(formattedValue);
            }
        }
    }, [value, editMode, formatValue, callbackArguments]);

    const handleClickDisplay = (e) => {
        if (readonly || showEditButton) return;
        e.stopPropagation(); // Manually added
        setEditMode(true);

        setTimeout(() => inputRef.current?.select()); // Manually added

        onEditMode();
    };

    const handleClickEditButton = (e) => {
        e.stopPropagation(); // Manually added
        setEditMode(true);

        setTimeout(() => inputRef.current?.select()); // Manually added

        onEditMode();
    };

    const handleBlur = (save = true) => {
        if (inputRef.current) {
            const { name: inputName, value: inputValue } = inputRef.current;
            if (save && previousFormattedValue !== inputValue) {
                try {
                    // Parse the value using the parseValue function
                    const parsedValue = parseValue(inputValue);
                    // Format the parsed value for display
                    const formattedValue = formatValue(parsedValue, callbackArguments);

                    onSave({
                        name: inputName,
                        value: parsedValue,
                        formattedValue: formattedValue,
                        previousValue: previousFormattedValue
                    });

                    // Update display with formatted value
                    setFormattedValue(formattedValue);
                    setPreviousFormattedValue(formattedValue);
                } catch (error) {
                    // If parsing fails, don't save and show error
                    console.error('Error parsing value:', error);
                    // Call onSave with error information so parent can handle it
                    onSave({
                        name: inputName,
                        value: previousFormattedValue, // Revert to previous value on parsing error
                        formattedValue: formattedValue,
                        previousValue: previousFormattedValue,
                        error: error.message
                    });
                    // Don't update saved text or previous value on error
                }
            } else if (!save) {
                onChange({
                    ...changeEvent,
                    target: changeEvent.target
                        ? { ...changeEvent.target, value: previousFormattedValue }
                        : { value: previousFormattedValue }
                });
            }
            setEditMode(false);
            onBlur();
        }
    };

    const handleKeydown = (e) => {
        if (e.key === 'Enter') {
            handleBlur();
        } else if (e.key === 'Escape') {
            handleBlur(false);
        }
    };

    const handleFocus = (e) => {
        if (type === 'text') {
            e.currentTarget.setSelectionRange(
                e.currentTarget.value.length,
                e.currentTarget.value.length
            );
        }
    };

    const renderDisplayMode = () => {
        return (
            <div
                className={classnames(styles.displayContainer, {
                    [styles.inline]: inline
                })}
            >
                <div
                    id={id}
                    className={classnames(
                        styles.label,
                        styles.shared,
                        {
                            [styles.placeholder]: placeholder && !formattedValue,
                            [styles.inline]: inline,
                            [styles.readonly]: readonly || showEditButton
                        },
                        className
                    )}
                    onClick={handleClickDisplay}
                    style={style}
                    aria-label='display component'
                >
                    {formattedValue || placeholder}
                </div>
                {showEditButton && !readonly && (
                    <button
                        type='button'
                        className={styles.editButton}
                        {...editButtonProps}
                        onClick={handleClickEditButton}
                    >
                        {editButtonContent}
                    </button>
                )}
            </div>
        );
    };

    const renderEditMode = (controlled) => {
        const sharedProps = {
            inputRef: inputRef,
            handleBlur: handleBlur,
            handleKeydown: handleKeydown,
            handleFocus: handleFocus,
            props: { id, inline, style, type, name },
            'aria-label': 'input component'
        };
        return controlled ? (
            <Input
                {...sharedProps}
                value={value}
                style={{ lineHeight: 1, width: '100%' }}
                onChange={(e) => {
                    setChangeEvent(e);
                    onChange(e);
                }}
                inputClassName={inputClassName}
            />
        ) : (
            <Input
                {...sharedProps}
                defaultValue={formattedValue}
                inputClassName={inputClassName}
            />
        );
    };

    return !readonly && editMode
        ? renderEditMode(value !== undefined && onChange !== undefined)
        : renderDisplayMode();
}

EditText.propTypes = EditTextPropTypes;